import React, { useState } from "react";
import {
  Wrench,
  Shield,
  Crosshair,
  Filter,
  Scale,
  X,
  Zap,
  Utensils,
  Droplets,
} from "lucide-react";

const RARITY_THEMES = {
  common: {
    border: "border-zinc-800",
    bg: "bg-zinc-900/50",
    text: "text-zinc-400",
    label: "COMMON",
  },
  rare: {
    border: "border-blue-600",
    bg: "bg-blue-900/10",
    text: "text-blue-400",
    label: "RARE",
    shadow: "shadow-[0_0_10px_rgba(59,130,246,0.2)]",
  },
  epic: {
    border: "border-purple-600",
    bg: "bg-purple-900/10",
    text: "text-purple-400",
    label: "EPIC",
    shadow: "shadow-[0_0_10px_rgba(168,85,247,0.2)]",
  },
  legendary: {
    border: "border-yellow-500",
    bg: "bg-yellow-900/10",
    text: "text-yellow-400",
    label: "LEGENDARY",
    shadow: "shadow-[0_0_15px_rgba(234,179,8,0.3)]",
  },
};

export default function InventoryArmory({
  inventory,
  onRepair,
  onRepairAll,
  onConsume,
}) {
  const [filterType, setFilterType] = useState("all");
  const [compareList, setCompareList] = useState([]);
  const sortBy = "rarity";
  const showDamagedOnly = false;

  const getItemImage = (slug) => `/assets/items/${slug || "Default"}.png`;

  const processedInventory = [];
  const perfectItems = {};

  (inventory || []).forEach((item) => {
    if (item.durability >= 100) {
      const key = `${item.item_id}_perfect`;
      if (!perfectItems[key]) {
        perfectItems[key] = { ...item, quantity: 0 };
        processedInventory.push(perfectItems[key]);
      }
      perfectItems[key].quantity += item.quantity;
    } else {
      for (let i = 0; i < item.quantity; i++) {
        processedInventory.push({
          ...item,
          quantity: 1,
          unique_id: `${item.item_id}_dmg_${i}_${Math.random()}`,
        });
      }
    }
  });

  const filteredItems = processedInventory
    .filter((i) => filterType === "all" || i.type === filterType)
    .filter((i) => !showDamagedOnly || i.durability < 100)
    .sort((a, b) => {
      if (sortBy === "rarity") {
        const order = { legendary: 3, epic: 2, rare: 1, common: 0 };
        return order[b.rarity || "common"] - order[a.rarity || "common"];
      }
      return (
        (b[
          sortBy === "dmg"
            ? "atk_power"
            : sortBy === "def"
              ? "def_power"
              : "price"
        ] || 0) -
        (a[
          sortBy === "dmg"
            ? "atk_power"
            : sortBy === "def"
              ? "def_power"
              : "price"
        ] || 0)
      );
    });

  const toggleCompare = (item) => {
    if (!["weapon", "armor", "mercenary", "dog"].includes(item.type)) return;
    const itemKey = item.unique_id || item.item_id;
    if (compareList.find((i) => (i.unique_id || i.item_id) === itemKey)) {
      setCompareList(
        compareList.filter((i) => (i.unique_id || i.item_id) !== itemKey),
      );
    } else if (compareList.length < 2) {
      setCompareList([...compareList, item]);
    }
  };

  const renderItemStats = (item) => {
    switch (item.type) {
      case "weapon":
      case "mercenary":
      case "dog":
        return (
          <div className="grid grid-cols-2 gap-2 w-full mt-2">
            <div className="bg-zinc-900/50 p-1 border border-zinc-800 flex flex-col items-center">
              <span className="text-[8px] text-zinc-500 font-bold uppercase flex items-center gap-1">
                <Crosshair size={10} className="text-red-500" /> ATK
              </span>
              <span className="text-md font-black text-white">
                {item.atk_power}
              </span>
            </div>
            <div className="bg-zinc-900/50 p-1 border border-zinc-800 flex flex-col items-center">
              <span className="text-[8px] text-zinc-500 font-bold uppercase flex items-center gap-1">
                <Shield size={10} className="text-blue-500" /> DEF
              </span>
              <span className="text-md font-black text-white">
                {item.def_power}
              </span>
            </div>
          </div>
        );
      case "consumable":
        return (
          <div className="bg-zinc-900/50 p-2 border border-zinc-800 w-full mt-2 flex justify-around">
            <div className="flex flex-col items-center">
              <span className="text-[7px] text-orange-500 font-black uppercase">
                Nutrition
              </span>
              <Utensils size={10} className="text-white" />
            </div>
            <div className="flex flex-col items-center border-l border-zinc-800 pl-4">
              <span className="text-[7px] text-blue-500 font-black uppercase">
                Hydration
              </span>
              <Droplets size={10} className="text-white" />
            </div>
          </div>
        );
      case "girl":
        return (
          <div className="w-full mt-2 bg-pink-900/20 p-1 border border-pink-900/50 flex flex-col items-center">
            <span className="text-lg font-black text-pink-200">
              +${(item.passive_income || 0).toLocaleString()}
            </span>
          </div>
        );
      default:
        return <div className="h-[42px] w-full mt-2"></div>;
    }
  };

  return (
    <div className="space-y-6 font-mono animate-in fade-in duration-500">
      {/* Compare Modal logic (inherited from previous version) */}
      {compareList.length === 2 && (
        <div className="bg-zinc-950 border-2 border-blue-600 p-6 relative z-20">
          <div className="flex justify-between mb-4">
            <h3 className="text-white font-black italic uppercase flex items-center gap-2">
              <Scale size={20} /> Tactical View
            </h3>
            <button onClick={() => setCompareList([])}>
              <X size={20} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {compareList.map((item, i) => (
              <div key={i} className="text-center">
                <p className="text-white font-black uppercase mb-2">
                  {item.name}
                </p>
                <div className="text-xs text-zinc-500">
                  ATK: {item.atk_power}
                  {" // "}
                  DEF: {item.def_power}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Control Bar */}
      <div className="flex flex-wrap gap-4 bg-zinc-950 p-4 border border-zinc-800 sticky top-0 z-10 items-center">
        <div className="flex items-center gap-2 bg-black border border-zinc-700 px-3 py-1">
          <Filter size={14} className="text-zinc-500" />
          <select
            className="bg-transparent text-[10px] text-white outline-none cursor-pointer uppercase"
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">Full Stash</option>
            <option value="weapon">Weapons</option>
            <option value="armor">Armor</option>
            <option value="consumable">Consumables</option>
            <option value="mercenary">Units</option>
          </select>
        </div>
        <button
          onClick={onRepairAll}
          className="ml-auto bg-green-600 text-black font-black px-6 py-1 text-[10px] uppercase italic transition-all"
        >
          <Wrench size={12} /> Repair All
        </button>
      </div>

      {/* Grid Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredItems.map((item, idx) => {
          const theme = RARITY_THEMES[item.rarity || "common"];
          const isComparing = compareList.some(
            (c) =>
              (c.unique_id || c.item_id) === (item.unique_id || item.item_id),
          );
          const repairCost = Math.floor(
            (item.price || 0) * 0.3 * ((100 - item.durability) / 100),
          );

          return (
            <div
              key={idx}
              onDoubleClick={() =>
                item.type === "consumable" && onConsume(item.id)
              }
              className={`relative bg-black border-2 transition-all flex flex-col justify-between ${isComparing ? "border-blue-500" : theme.border}`}
            >
              <div
                className={`p-2 border-b ${theme.border} ${theme.bg} flex justify-between items-center`}
              >
                <span
                  className={`text-[9px] font-black uppercase px-2 py-0.5 border ${theme.border} ${theme.text} bg-black`}
                >
                  {theme.label}
                </span>
                <span className="text-xs font-bold text-white bg-black px-2 py-1 border border-zinc-800">
                  x{item.quantity}
                </span>
              </div>
              <div className="p-4 space-y-3 flex-1 flex flex-col items-center">
                <div className="w-full h-24 flex items-center justify-center bg-zinc-900/30 rounded-lg relative overflow-hidden group-hover:bg-zinc-900/50">
                  <img
                    src={getItemImage(item.icon_slug)}
                    alt={item.name}
                    className="w-full h-full object-contain p-2"
                    onError={(e) =>
                      (e.target.src = "/assets/items/Default.png")
                    }
                  />
                </div>
                <h4 className="text-sm font-black text-white uppercase italic truncate w-full text-center">
                  {item.name}
                </h4>
                {renderItemStats(item)}
              </div>

              {/* ACTION FOOTER: Grid restored, no logic deleted */}
              <div className="p-3 bg-zinc-950 border-t border-zinc-900 grid grid-cols-4 gap-2">
                {["weapon", "armor", "mercenary", "dog"].includes(item.type) ? (
                  <button
                    onClick={() => toggleCompare(item)}
                    className={`col-span-1 flex items-center justify-center border transition-all h-8 ${isComparing ? "bg-blue-600 border-blue-400" : "bg-zinc-900 border-zinc-800 text-zinc-500"}`}
                  >
                    <Scale size={14} />
                  </button>
                ) : (
                  <div className="col-span-1 bg-zinc-950/50 border border-zinc-900"></div>
                )}

                {item.type === "consumable" ? (
                  <button
                    onClick={() => onConsume(item.id)}
                    className="col-span-3 bg-blue-700 hover:bg-blue-600 text-white font-black uppercase text-[10px] italic flex items-center justify-center gap-2 border border-blue-600 shadow-[0_0_15px_rgba(37,99,235,0.3)]"
                  >
                    <Zap size={12} /> Use Item
                  </button>
                ) : item.durability < 100 &&
                  ["weapon", "armor", "mercenary", "dog"].includes(
                    item.type,
                  ) ? (
                  <button
                    onClick={() => onRepair(item.item_id)}
                    className="col-span-3 bg-green-700 hover:bg-green-600 text-white font-black uppercase text-[10px] italic flex items-center justify-center gap-2 border border-green-600"
                  >
                    <Wrench size={12} /> Repair (${repairCost})
                  </button>
                ) : (
                  <div className="col-span-3 bg-zinc-900 border border-zinc-800 text-zinc-600 font-bold uppercase text-[10px] flex items-center justify-center italic opacity-50 cursor-default">
                    {["collectible", "girl", "cook", "runner"].includes(
                      item.type,
                    )
                      ? "Secure"
                      : "Mint"}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
